import { useQuery } from '@tanstack/react-query';
import api from '../services/axiosInstance';
import { API_ENDPOINTS } from '../config/api';

export interface ProcurementPerformance {
  procurement_id: number;
  identifier: string;
  procurement_number: string;
  date_procured: string;
  bird_colors: string;
  egg_production: {
    total_eggs: number;
    total_crates: number;
    production_share_pct: number;
  };
  financials: {
    procurement_cost: number;
    direct_expenditures: number;
    allocated_expenditures: number;
    salary_expenditures: number;
    total_expenditures: number;
    total_sales: number;
    profit: number;
    profit_margin: number;
  };
}

export interface CyclePerformanceData {
  cycle: {
    id: number;
    cycle_number: string;
    start_date: string;
    end_date: string;
    is_active: boolean;
  };
  procurements: ProcurementPerformance[];
  summary: {
    total_procurements: number;
    total_eggs: number;
    total_expenditures: number;
    total_salary_expenditures: number;
    total_sales: number;
    total_profit: number;
    overall_profit_margin: number;
    unallocated_expenditures: number;
  };
}

export interface BirdBreakdown {
  bird_id: number;
  identifier: string;
  color: string;
  quantity_bought: number;
  current_quantity: number;
  egg_production: {
    total_eggs: number;
    total_crates: number;
    production_share_pct: number;
  };
  financials: {
    revenue: number;
    direct_cost: number;
    salary_cost: number;
    overhead_cost: number;
    total_cost: number;
    profit: number;
    profit_margin: number;
  };
}

export const useCyclePerformance = (cycleId: number | null) => {
  return useQuery({
    queryKey: ['cycle-performance', cycleId],
    queryFn: async () => {
      if (!cycleId) return null;
      const response = await api.get(`/financial-cycles/${cycleId}/procurement-performance`);
      return response.data.data as CyclePerformanceData;
    },
    enabled: !!cycleId,
  });
};

export const useProcurementBirdsBreakdown = (
  cycleId: number | null,
  procurementId: number | null
) => {
  return useQuery({
    queryKey: ['cycle-procurement-birds', cycleId, procurementId],
    queryFn: async () => {
      const response = await api.get(
        API_ENDPOINTS.FINANCIAL_CYCLES.PROCUREMENT_BIRDS(cycleId ?? 0, procurementId ?? 0)
      );
      return response.data.data.birds as BirdBreakdown[];
    },
    enabled: !!cycleId && !!procurementId,
  });
};
